import React, { useEffect } from 'react';

import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Link from '@amzn/meridian/link';
import Text from '@amzn/meridian/text';
import { useQuery } from '@tanstack/react-query';
import _cloneDeep from 'lodash/cloneDeep';
import _includes from 'lodash/includes';
import { SUBMIT_FEATURE_REQUEST_AND_BUG_URL } from 'src/config/ExternalLinks';
import { TOAST_TIMEOUT } from 'src/config/Toast';
import { getTaskQueryOptions } from 'src/services/tasks/taskQueries';
import {
  getRegionTasksById,
  setIsCancelModalOpen,
  setIsRestrictionsPageReadOnly,
  setPageProgress,
  setRestrictionListItems,
  setSelectedLimitations,
  setSelectedTasks,
} from 'src/store/newJmrPageSlice/newJmrPageSlice';
import { type JmrTask } from 'src/store/newJmrPageSlice/types';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { closeAllToastsForMessage, createToastIfNoneForMessage } from 'src/store/toastsSlice';
import { isNotEmpty } from 'src/utils/helpers';
import { JmrFlowTypes, JmrPageProgress } from 'src/utils/jmrUtils';
import { nonPhysicalTasksToLimitationOptionsList, validateSelectedLimitations } from 'src/utils/restrictionUtils';
import {
  isParentTask,
  isRestrictionListTask,
  type RestrictionListItem,
  validateRestrictionListItems,
} from 'src/utils/restrictionUtilsLegacy';
import { setupPreselectedRestrictionListItems } from 'src/utils/restrictionUtilsLegacy';

import { NewJMRPageFooter, NewJMRPageMainBody, NewJmrPageTitle, NewJMRPageWrapper } from '../../pageLayout/pageLayout';
import PlacementCountAlert from '../../placementCountAlert/placementCountAlert';
import NonPhysicalLimitations from '../nonPhysicalLimitations/nonPhysicalLimitations';
import AllTasksSelector from '../restrictionPageNewExperience/allTasksSelector';

import RestrictionList from './restrictionList';

import AllTasksSelectorLegacy from './selectors/allTasksSelector';

// **** Constants **** //

// Exporting for texting
export const TOAST_MESSAGE = 'Please see the errors below, and try clicking "Next" again';

// **** Functional Components **** //

/**
 * Main component.
 */
const RestrictionsPage = () => {
  const dispatch = useAppDispatch();
  const { featureFlags } = useAppSelector((state) => state.user);
  const {
    restrictionListItems,
    selectedLimitations,
    isRestrictionsPageReadOnly,
    jmrFlowType,
    selectedTasks,
    consolidatedRegionTasks,
    backgroundFormData,
  } = useAppSelector((state) => state.newJmrPage);

  const { data: limitationOptions, isSuccess: isLimitationsQuerySuccess } = useQuery({
    ...getTaskQueryOptions.nonPhysicalTasksByRegion(backgroundFormData.site?.regionId),
    enabled: !!backgroundFormData.site?.regionId,
    select: nonPhysicalTasksToLimitationOptionsList,
  });

  const nonPhysicalLimitationsEnabled =
    featureFlags.NON_PHYSICAL_LIMITATIONS && backgroundFormData.site.nonPhysicalLimitationsEnabled;

  const allFormattedNonPhysicalTaskOptions = (limitationOptions || []).map((o) => ({
    label: o.name,
    value: o.id.toString(),
    key: o.id.toString(),
  }));

  // If updating a Placement, set the list of pre-selected items when the group of
  // selected-tasks change
  useEffect(() => {
    const { CompleteTransfer, UpdateDates, AlternativePlacementRecommended } = JmrFlowTypes;
    const shouldReuseExistingRestrictions = _includes(
      [CompleteTransfer, UpdateDates, AlternativePlacementRecommended],
      jmrFlowType,
    );
    if (selectedTasks.length > 0 && shouldReuseExistingRestrictions) {
      const items = setupPreselectedRestrictionListItems(selectedTasks, consolidatedRegionTasks);
      dispatch(setRestrictionListItems(items));
    }
  }, [consolidatedRegionTasks, selectedTasks]);

  // Fetch tasks based on region id of the selected site. Note that the regionTasks
  // is used on the restrictions-pages for the list of tasks (aka restrictions).
  useEffect(() => {
    const regionId = backgroundFormData.site?.regionId;
    if (regionId !== undefined) {
      dispatch(getRegionTasksById(regionId));
    }
  }, [backgroundFormData.site?.regionId]);

  // clear stale limitations
  useEffect(() => {
    if (!nonPhysicalLimitationsEnabled && selectedLimitations.length) {
      dispatch(setSelectedLimitations([]));
    }
  }, [nonPhysicalLimitationsEnabled, selectedLimitations]);

  // List for flow-type updates
  useEffect(() => {
    dispatch(setIsRestrictionsPageReadOnly(jmrFlowType === JmrFlowTypes.UpdateDates));
  }, [jmrFlowType]);

  // Generate job matches and navigate to the next page
  const goToJobMatchesPage = () => {
    const items = _cloneDeep(restrictionListItems);
    let isValid = true;
    if (!isRestrictionsPageReadOnly) {
      const { isValid: areRestrictionsValid, updatedList: updatedRestrictions } = validateRestrictionListItems(items);
      const { isValid: areLimitationsValid, updatedList: updatedLimitations } =
        validateSelectedLimitations(selectedLimitations);
      dispatch(setRestrictionListItems(updatedRestrictions));
      dispatch(setSelectedLimitations(updatedLimitations));
      isValid = areRestrictionsValid && areLimitationsValid;
    }
    if (isValid) {
      const jmrTasks = restrictionItemsListToJmrTaskList(items);
      dispatch(setSelectedTasks(jmrTasks));
      dispatch(closeAllToastsForMessage(TOAST_MESSAGE));
      dispatch(setPageProgress(JmrPageProgress.JobMatches));
    } else {
      dispatch(
        createToastIfNoneForMessage({
          type: 'error',
          message: TOAST_MESSAGE,
          timeout: TOAST_TIMEOUT,
        }),
      );
    }
  };

  const setLimitations = (entityIds: string[]) => {
    if (!isLimitationsQuerySuccess) return;
    dispatch(
      setSelectedLimitations(
        entityIds.map(
          (entityId) =>
            selectedLimitations.find((l) => l.id.toString() === entityId) ??
            limitationOptions.find((l) => l.id.toString() === entityId)!,
        ),
      ),
    );
  };

  return (
    <NewJMRPageWrapper>
      {/* Main Content */}
      <NewJMRPageMainBody xs="85%" md="75%" lg="60%">
        <Column spacing="450" spacingInset="300">
          <PlacementCountAlert />
          <NewJmrPageTitle
            title="Add and Edit Restriction(s)"
            subtitle={
              nonPhysicalLimitationsEnabled ? (
                <>
                  Please enter all physical restrictions and non-physical limitations as documented in the associate's
                  medical paperwork (RFI, APF, WSR, etc.). Non-physical limitations do not affect the job match results
                  and are for record keeping only. Need to request a new non-physical restriction option? Submit a{' '}
                  <Link type="secondary" target="_blank" href={SUBMIT_FEATURE_REQUEST_AND_BUG_URL}>
                    SIM ticket
                  </Link>
                  .
                </>
              ) : (
                'Please add and enter all restrictions as denoted within the associates RFI.'
              )
            }
          />
          <span style={{ pointerEvents: isRestrictionsPageReadOnly ? 'none' : 'auto' }}>
            <Column spacing="300">
              <Text>Restrictions</Text>
              <AllTasksSelectorLegacy />
              {nonPhysicalLimitationsEnabled ? (
                <AllTasksSelector
                  label="Non-Physical Limitations"
                  dataTestId="new-jmr__limitations__all-tasks__dropdown-selector"
                  isDisabled={isRestrictionsPageReadOnly}
                  availableOptions={allFormattedNonPhysicalTaskOptions}
                  selectedOptions={selectedLimitations.map((item) => item.id.toString())}
                  onSelectTasks={setLimitations}
                />
              ) : null}
            </Column>

            <Column spacingInset="400 none" spacing="400">
              <RestrictionList />
              {nonPhysicalLimitationsEnabled ? <NonPhysicalLimitations /> : null}
            </Column>
          </span>
        </Column>
      </NewJMRPageMainBody>

      {/* Footer */}
      <NewJMRPageFooter
        leftSideContent={
          <Column spacing="200">
            <Text>{restrictionListItems.filter((t) => !!t.label).length} Physical Restriction(s)</Text>
            {nonPhysicalLimitationsEnabled ? (
              <Text>{selectedLimitations.length} Non-Physical Limitation(s)</Text>
            ) : null}
          </Column>
        }
      >
        <Button type="link" onClick={() => dispatch(setIsCancelModalOpen(true))}>
          Cancel
        </Button>
        <Button type="tertiary" onClick={() => dispatch(setPageProgress(JmrPageProgress.Background))}>
          Previous
        </Button>
        <Button data-cy="new-jmr__restrictions__next" onClick={goToJobMatchesPage}>
          Next
        </Button>
      </NewJMRPageFooter>
    </NewJMRPageWrapper>
  );
};

/**
 * Convert RestrictionItemsList to JmrTask (selectedTasks) List.
 */
const restrictionItemsListToJmrTaskList = (list: RestrictionListItem[]): JmrTask[] => {
  const consolidatedTasks = list.filter(isParentTask);
  const individualTasks = list.filter(isRestrictionListTask);
  const unpackedConsolidatedTasks = consolidatedTasks.flatMap((task) => task.children);
  const allTasks = [...unpackedConsolidatedTasks, ...individualTasks];
  return allTasks
    .filter((t) => isNotEmpty(t.restrictedValue))
    .map(
      (task) =>
        ({
          regionId: task.regionId,
          taskId: task.taskId,
          rank: task.rank,
          label: task.label,
          description: task.description,
          identifyBodyLocation: task.identifyBodyLocation,
          measureUnit: task.measureUnit,
          restrictedValue: task.restrictedValue,
          ...(task.bodyLocationValue
            ? {
                bodyLocationValue: task.bodyLocationValue,
              }
            : {}),
        }) as JmrTask,
    );
};

// **** Export Default **** //

export default RestrictionsPage;
